import { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { PanelLeftClose, PanelLeftOpen, PanelRightClose, PanelRightOpen } from 'lucide-react';
import { api, deleteSingleChatMessage, getSysPrompts, getToken } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { WorkshopHeader } from '../components/workshop-plan/WorkshopHeader';
import { ChatArea } from '../components/workshop-plan/ChatArea';
import { DocumentEditor } from '../components/workshop-plan/DocumentEditor';
import { usePlanVersionManager } from '../hooks/usePlanVersionManager';

type Message = {
  id?: number;
  role: 'user' | 'assistant';
  content: string;
  input_tokens?: number;
  output_tokens?: number;
  input_money?: number;
  output_money?: number;
  // 流式消息相关：
  reasoningText?: string;
  isThinking?: boolean;
  partial?: boolean;
  partialReason?: string;
};

type SysPrompt = {
  id: number;
  name: string;
  prompt_content?: string;
  category: string;
  updated_at: string;
};

type LessonInfo = {
  title: string;
  stageTitle: string;
  sortOrder: number;
};

type MultiAgentProgress = {
  current_stage: string;
  phase_index: number;
  last_updated_at: string;
};

type MultiAgentProgressResponse = {
  has_progress: boolean;
  progress: MultiAgentProgress | null;
  content_length?: number;
};

export default function WorkshopPlan() {
  const { stageId, lessonId: lessonIdStr, artifactId: artifactIdStr } = useParams();
  const lessonId = parseInt(lessonIdStr || '0', 10);
  const artifactId = parseInt(artifactIdStr || '0', 10);
  const { user } = useAuth();
  const location = useLocation();

  const backCourseId = location.state?.backCourseId ? Number(location.state.backCourseId) : null;

  const {
    plans,
    currentPlan,
    localContent,
    isContentChanged,
    isSyncing: isPlanSyncing,
    actions: planActions,
  } = usePlanVersionManager(lessonId, artifactId);

  const isCurrentUserOwner = (currentPlan && user && currentPlan.created_by === user.id) ?? undefined;

  const [isConnected, setIsConnected] = useState(false);
  const [lessonInfo, setLessonInfo] = useState<LessonInfo | null>(null);
  const [isRightPanelCollapsed, setIsRightPanelCollapsed] = useState(false);
  const [isLeftPanelCollapsed, setIsLeftPanelCollapsed] = useState(false);
  const [prompts, setPrompts] = useState<SysPrompt[]>([]);
  const [selectedPromptId, setSelectedPromptId] = useState<number | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isAgentGenerating, setIsAgentGenerating] = useState(false);
  // 走马灯字幕：reasoning/progress 不存入消息，独立状态显示
  const [currentReasoning, setCurrentReasoning] = useState('');
  const [hasMultiAgentProgress, setHasMultiAgentProgress] = useState(false);
  const [multiAgentProgressData, setMultiAgentProgressData] = useState<MultiAgentProgress | null>(null);
  const [resolvedCourseId, setResolvedCourseId] = useState<number | null>(null);

  const ws = useRef<WebSocket | null>(null);
  const agentWs = useRef<WebSocket | null>(null);
  const mounted = useRef(true);

  const activeMode = useMemo(() => {
    const prompt = prompts.find(item => item.id === selectedPromptId);
    return prompt?.category || 'gen_plan';
  }, [prompts, selectedPromptId]);

  const isBusy = isGenerating || isAgentGenerating;

  const effectiveCourseId = backCourseId ?? resolvedCourseId;

  const getPromptIdByCourseId = (courseId: number | null): number | null => {
    if (courseId === 5) return 9;
    if (courseId === 6) return 16;
    return null;
  };

  const getPhaseCountByCourseId = (courseId: number | null): number => {
    if (courseId === 5) return 7;
    if (courseId === 6) return 10;
    return 7;
  };

  const getEffectivePromptId = (): number | null => {
    const courseBasedPromptId = getPromptIdByCourseId(effectiveCourseId);
    if (courseBasedPromptId !== null) return courseBasedPromptId;
    return selectedPromptId;
  };

  const handleAppendContent = (text: string) => {
    planActions.appendContent(text);
  };

  const handleSaveContent = () => {
    planActions.savePlan();
  };

  const handleExportMessage = async (content: string) => {
    const defaultFileName = `AI回复_${new Date().toLocaleString('zh-CN').replace(/[/:]/g, '-')}.txt`;
    
    try {
      if ('showSaveFilePicker' in window) {
        const handle = await (window as any).showSaveFilePicker({
          suggestedName: defaultFileName,
          types: [{
            description: '文本文件',
            accept: { 'text/plain': ['.txt'] }
          }]
        });
        const writable = await handle.createWritable();
        await writable.write(content);
        await writable.close();
      } else {
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = defaultFileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
      }
    } catch (e: any) {
      if (e.name !== 'AbortError') {
        console.error('导出失败:', e);
        alert(`导出失败: ${e.message || '未知错误'}`);
      }
    }
  };

  const addToDraft = (text: string) => {
    planActions.appendContent(text);
  };

  const getWsBase = () => {
    const apiBaseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api/v1';

    try {
      const url = new URL(apiBaseUrl);
      return {
        protocol: url.protocol === 'https:' ? 'wss:' : 'ws:',
        host: url.host,
      };
    } catch (e) {
      const currentUrl = new URL(window.location.href);
      return {
        protocol: currentUrl.protocol === 'https:' ? 'wss:' : 'ws:',
        host: currentUrl.host,
      };
    }
  };

  const buildWsUrl = (path: string) => {
    const { protocol, host } = getWsBase();
    const token = getToken();
    return `${protocol}//${host}${path}${token ? `?token=${token}` : ''}`;
  };

  const finalizeAssistantMeta = (prev: Message[], data: any, partial?: boolean, partialReason?: string) => {
    const next = [...prev];
    let assistantIndex = -1;

    for (let i = next.length - 1; i >= 0; i -= 1) {
      if (next[i].role === 'assistant') {
        assistantIndex = i;
        break;
      }
    }

    if (assistantIndex === -1) {
      return next;
    }

    next[assistantIndex] = {
      ...next[assistantIndex],
      id: data.id ?? next[assistantIndex].id,
      input_tokens: data.input_tokens ?? next[assistantIndex].input_tokens,
      output_tokens: data.output_tokens ?? next[assistantIndex].output_tokens,
      input_money: data.input_money ?? next[assistantIndex].input_money,
      output_money: data.output_money ?? next[assistantIndex].output_money,
      total_money: data.total_money ?? next[assistantIndex].total_money,
      partial: partial,
      partialReason: partialReason,
    };

    if (assistantIndex > 0 && next[assistantIndex - 1].role === 'user') {
      next[assistantIndex - 1] = {
        ...next[assistantIndex - 1],
        id: data.user_message_id ?? next[assistantIndex - 1].id ?? (typeof data.id === 'number' ? data.id - 1 : undefined),
      };
    }

    return next;
  };

  const fetchChatHistoryByMode = async (mode: string) => {
    if (!lessonId || !artifactId) return;
    try {
      const response = await api.get(`/workshop/chat/history/${mode}/${lessonId}/${artifactId}/`);
      if (Array.isArray(response.data)) {
        setMessages(response.data);
      }
    } catch (e) {
      console.warn('History load failed', e);
    }
  };

  const fetchChatHistory = async () => {
    await fetchChatHistoryByMode(activeMode);
  };

  const refreshCurrentPlanFromServer = async (planId?: number) => {
    try {
      await planActions.refreshCurrentPlan(planId || currentPlan?.id || undefined);
    } catch (e) {
      console.warn('Plan refresh failed', e);
    }
  };

  const getPreferredMultiAgentPromptId = () => {
    const courseBasedPromptId = getPromptIdByCourseId(effectiveCourseId);
    if (courseBasedPromptId !== null) return courseBasedPromptId;

    const selectedPrompt = prompts.find(prompt => prompt.id === selectedPromptId);
    if (selectedPrompt && !selectedPrompt.category?.toLowerCase().startsWith('audit')) {
      return selectedPrompt.id;
    }

    const fallbackPrompt = prompts.find(prompt => !prompt.category?.toLowerCase().startsWith('audit'));
    return fallbackPrompt?.id ?? selectedPromptId ?? null;
  };

  const fetchMultiAgentProgress = async () => {
    if (!lessonId || !artifactId) return;
    try {
      const response = await api.get<MultiAgentProgressResponse>(
        `/workshop/multi-agent/progress/${lessonId}/${artifactId}`
      );
      setHasMultiAgentProgress(response.data.has_progress);
      setMultiAgentProgressData(response.data.progress);
    } catch (e) {
      console.warn('Failed to fetch multi-agent progress', e);
      setHasMultiAgentProgress(false);
      setMultiAgentProgressData(null);
    }
  };

  const clearMultiAgentProgress = async (full: boolean = true) => {
    if (!lessonId || !artifactId) return;
    try {
      await api.delete(`/workshop/multi-agent/progress/${lessonId}/${artifactId}?full=${full}`);
      setHasMultiAgentProgress(false);
      setMultiAgentProgressData(null);
    } catch (e) {
      console.warn('Failed to clear multi-agent progress', e);
    }
  };

  const fetchBasicData = async () => {
    try {
      const promptResponse = await getSysPrompts();
      const planPrompts = promptResponse.data.filter((prompt: any) =>
        prompt.category && typeof prompt.category === 'string' && prompt.category.toLowerCase().endsWith('plan'),
      );
      setPrompts(planPrompts);
      if (planPrompts.length > 0) {
        setSelectedPromptId(planPrompts[0].id);
      }

      if (stageId && lessonId) {
        const [stageRes, lessonRes] = await Promise.all([
          api.get(`/courses/stages/${stageId}/`),
          api.get(`/courses/lessons/${lessonId}/`),
        ]);

        setLessonInfo({
          title: lessonRes.data.title,
          stageTitle: stageRes.data.title,
          sortOrder: lessonRes.data.sort_order,
        });

        if (stageRes.data.course_id) {
          setResolvedCourseId(stageRes.data.course_id);
        }
      }

      void fetchMultiAgentProgress();
    } catch (e) {
      console.error('Init Error:', e);
    }
  };

  useEffect(() => {
    mounted.current = true;
    void fetchBasicData();
    return () => {
      mounted.current = false;
      if (ws.current) {
        ws.current.close();
        ws.current = null;
      }
      if (agentWs.current) {
        agentWs.current.close();
        agentWs.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!lessonId || !artifactId) return;
    if (prompts.length === 0) return;

    setMessages([]);
    void fetchChatHistory();

    if (ws.current) {
      ws.current.close();
      ws.current = null;
      setIsConnected(false);
    }

    const timer = window.setTimeout(() => {
      connectWebSocket();
    }, 100);

    return () => window.clearTimeout(timer);
  }, [activeMode, lessonId, artifactId, prompts.length]);

  const connectWebSocket = () => {
    if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const socket = new WebSocket(buildWsUrl(`/api/v1/workshop/ws/generate/${activeMode}/${lessonId}/${artifactId}`));
    ws.current = socket;

    socket.onopen = () => {
      if (mounted.current) {
        setIsConnected(true);
      }
    };

    socket.onclose = () => {
      if (mounted.current) {
        setIsConnected(false);
      }
      ws.current = null;
    };

    socket.onerror = () => {
      if (mounted.current) {
        setIsConnected(false);
      }
    };

    socket.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        // 走马灯字幕：reasoning / progress / continuation_waiting 不存入消息，独立状态显示
        if (data.type === 'reasoning' || data.type === 'progress' || data.type === 'continuation_waiting') {
          setCurrentReasoning(data.content || '');
          return;
        }

        if (data.type === 'chunk') {
          setCurrentReasoning('');
          setMessages(prev => {
            const last = prev[prev.length - 1];
            if (last?.role === 'assistant') {
              return [...prev.slice(0, -1), { ...last, content: last.content + data.content }];
            }
            return [...prev, { role: 'assistant', content: data.content }];
          });
          return;
        }

        if (data.type === 'meta') {
          setIsGenerating(false);
          setCurrentReasoning('');

          if (['429 Too Many Requests', '504 Gateway Timeout', '400 Bad Request'].includes(data.error)) {
            alert(`请求失败，请稍后再试\n错误原因：${data.error_details || data.error}\n请刷新页面后重试`);
            setMessages(prev => {
              const next = [...prev];
              if (next.length > 0 && next[next.length - 1].role === 'assistant') {
                next.pop();
              }
              return next;
            });
            return;
          }

          if (data.partial === true) {
            console.warn(`[WorkshopPlan WS] 内容可能不完整: partial=true, reason=${data.partial_reason || 'unknown'}`);
          }

          if (typeof data.id === 'number') {
            setMessages(prev => finalizeAssistantMeta(prev, data, data.partial, data.partial_reason));
          }
          return;
        }

        if (data.type === 'stop_ack') {
          setIsGenerating(false);
          setCurrentReasoning('');
          if (typeof data.id === 'number' || data.input_tokens !== undefined) {
            setMessages(prev => finalizeAssistantMeta(prev, data, data.partial ?? true, data.partial_reason ?? 'user_cancelled'));
          }
          // 关闭旧 WS，重连新 WS，等待新聊天
          setTimeout(() => {
            if (ws.current) {
              ws.current.close();
              ws.current = null;
            }
            connectWebSocket();
          }, 100);
          return;
        }

        if (data.type === 'error') {
          setCurrentReasoning('');
          alert(`AI Error: ${data.content}`);
          setMessages(prev => {
            const next = [...prev];
            if (next.length > 0 && next[next.length - 1].role === 'assistant') {
              next.pop();
            }
            return next;
          });
          setIsGenerating(false);
        }
      } catch (e) {
        console.error('WS Parse', e);
      }
    };
  };

  const handleMultiAgentSocketMessage = (data: any) => {
    if (data.type === 'progress' || data.type === 'continuation_waiting') {
      // 走马灯字幕：progress / continuation_waiting 不存入消息，独立状态显示
      setCurrentReasoning(data.content || '');
      return;
    }

    if (data.type === 'history_sync') {
      void fetchChatHistoryByMode('gen_plan');
      return;
    }

    if (data.type === 'plan_saved') {
      void refreshCurrentPlanFromServer(data.artifact_id);
      return;
    }

    if (data.type === 'user') {
      setMessages(prev => [...prev, { role: 'user', id: data.id, content: data.content }, { role: 'assistant', content: '' }]);
      return;
    }

    if (data.type === 'chunk') {
      setCurrentReasoning('');
      setMessages(prev => {
        const last = prev[prev.length - 1];
        if (last?.role === 'assistant') {
          return [...prev.slice(0, -1), { ...last, content: last.content + data.content }];
        }
        return [...prev, { role: 'assistant', content: data.content }];
      });
      return;
    }

    if (data.type === 'meta') {
      setCurrentReasoning('');
      if (data.partial === true) {
        console.warn(`[Multi-Agent WS] 内容可能不完整: partial=true, reason=${data.partial_reason || 'unknown'}`);
      }
      if (typeof data.id === 'number') {
        setMessages(prev => finalizeAssistantMeta(prev, data, data.partial, data.partial_reason));
      }
      if (data.history_sync) {
        void fetchChatHistoryByMode('gen_plan');
      }
      return;
    }

    if (data.type === 'done') {
      setCurrentReasoning('');
      setIsAgentGenerating(false);
      void fetchChatHistoryByMode('gen_plan');
      void refreshCurrentPlanFromServer(data.artifact_id);
      return;
    }

    if (data.type === 'stop_ack') {
      setCurrentReasoning('');
      setIsAgentGenerating(false);
      void fetchChatHistoryByMode('gen_plan');
      void refreshCurrentPlanFromServer(data.artifact_id);
      return;
    }

    if (data.type === 'error') {
      setCurrentReasoning('');
      alert(`Multi-Agent Error: ${data.content}`);
      setIsAgentGenerating(false);
    }
  };

  const connectMultiAgentWebSocket = (promptId: number) => {
    if (agentWs.current && (agentWs.current.readyState === WebSocket.OPEN || agentWs.current.readyState === WebSocket.CONNECTING)) {
      return;
    }

    const socket = new WebSocket(buildWsUrl(`/api/v1/workshop/ws/multi-agent/generate/${lessonId}/${artifactId}`));
    agentWs.current = socket;

    socket.onopen = () => {
      if (!mounted.current) return;
      setMessages([]);
      setIsAgentGenerating(true);
      socket.send(JSON.stringify({
        type: 'start',
        prompt_id: promptId,
        model: 'gemini',
      }));
    };

    socket.onmessage = (event) => {
      try {
        handleMultiAgentSocketMessage(JSON.parse(event.data));
      } catch (e) {
        console.error('Multi-Agent WS Parse', e);
      }
    };

    socket.onerror = () => {
      if (mounted.current) {
        setIsAgentGenerating(false);
      }
    };

    socket.onclose = () => {
      if (mounted.current) {
        setIsAgentGenerating(false);
      }
      agentWs.current = null;
    };
  };

  const sendMessage = (text: string, promptId?: number, clearHistory: boolean = false) => {
    if (isAgentGenerating) {
      alert('多Agent生成进行中，请先停止当前任务。');
      return;
    }

    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
      if (!ws.current || ws.current.readyState === WebSocket.CLOSED) {
        console.log('WebSocket disconnected, attempting to reconnect...');
        connectWebSocket();
        setTimeout(() => {
          if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            sendMessage(text, promptId, clearHistory);
          } else {
            alert('连接已断开，请刷新页面重试');
          }
        }, 2000);
      } else {
        alert('正在连接中，请稍后再试');
      }
      return;
    }

    const effectivePromptId = promptId || getEffectivePromptId();

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setIsGenerating(true);
    setMessages(prev => [...prev, { role: 'assistant', content: '' }]);
    ws.current.send(JSON.stringify({
      content: text,
      prompt_id: effectivePromptId,
      clear_history: clearHistory,
    }));
  };

  const handleSendClick = () => {
    if (!input.trim() || isBusy) return;
    sendMessage(input);
  };

  const handleStart = async () => {
    const effectivePromptId = getEffectivePromptId();
    if (!effectivePromptId) return alert('请先选择一个系统提示词（SOP）');
    if (!lessonInfo) return alert('课程信息加载中...');
    if (isAgentGenerating) return alert('多Agent生成进行中，请先停止当前任务。');

    setMessages([]);
    try {
      await api.delete(`/workshop/chat/clear/${activeMode}/${lessonId}/${artifactId}/`);
    } catch (e) {
      console.warn('Clear history failed', e);
    }

    let startMsg = '';
    const isAuditMode = activeMode.includes('audit');
    const currentContent = localContent;

    if (isAuditMode) {
      startMsg = `开始审核【${lessonInfo.stageTitle}】中第【${lessonInfo.sortOrder}】课教案`;
      startMsg += currentContent ? `\n\n【待审核教案全文】：\n${currentContent}` : '\n\n（注意：当前还没有教案草稿）';
    } else {
      startMsg = `开始开发【${lessonInfo.stageTitle}】中第【${lessonInfo.sortOrder}】课教案`;
      startMsg += currentContent ? `\n\n（参考旧版本草稿）：\n${currentContent.substring(0, 1000)}...` : '';
    }

    sendMessage(startMsg, effectivePromptId, true);
  };

  const handleRunMultiAgent = () => {
    if (!lessonId || !artifactId) return alert('当前教案版本无效。');
    if (!currentPlan?.id) return alert('请先选择一个可编辑教案版本。');
    if (!isCurrentUserOwner) return alert('只有当前版本创建者才能启动多Agent生成。');
    if (isPlanSyncing) return alert('当前正在同步教案内容，请稍后再试。');
    if (isAgentGenerating) return;

    const promptId = getPreferredMultiAgentPromptId();
    if (!promptId) {
      alert('没有找到可用于教案生成的系统提示词。');
      return;
    }

    if (promptId !== selectedPromptId) {
      setSelectedPromptId(promptId);
    }

    connectMultiAgentWebSocket(promptId);
  };

  const handleContinueMultiAgent = () => {
    if (!lessonId || !artifactId) return alert('当前教案版本无效。');
    if (!currentPlan?.id) return alert('请先选择一个可编辑教案版本。');
    if (!isCurrentUserOwner) return alert('只有当前版本创建者才能启动多Agent生成。');
    if (isPlanSyncing) return alert('当前正在同步教案内容，请稍后再试。');
    if (isAgentGenerating) return;

    const promptId = getPreferredMultiAgentPromptId();
    if (!promptId) {
      alert('没有找到可用于教案生成的系统提示词。');
      return;
    }

    if (promptId !== selectedPromptId) {
      setSelectedPromptId(promptId);
    }

    connectMultiAgentWebSocket(promptId);
  };

  const handleRestartMultiAgent = async () => {
    if (!lessonId || !artifactId) return alert('当前教案版本无效。');
    if (!currentPlan?.id) return alert('请先选择一个可编辑教案版本。');
    if (!isCurrentUserOwner) return alert('只有当前版本创建者才能启动多Agent生成。');
    if (isPlanSyncing) return alert('当前正在同步教案内容，请稍后再试。');
    if (isAgentGenerating) return;

    const promptId = getPreferredMultiAgentPromptId();
    if (!promptId) {
      alert('没有找到可用于教案生成的系统提示词。');
      return;
    }

    if (promptId !== selectedPromptId) {
      setSelectedPromptId(promptId);
    }

    await clearMultiAgentProgress(true);
    connectMultiAgentWebSocket(promptId);
  };

  const handleStopMultiAgent = () => {
    if (!agentWs.current || agentWs.current.readyState !== WebSocket.OPEN) {
      setIsAgentGenerating(false);
      return;
    }

    agentWs.current.send(JSON.stringify({ type: 'stop' }));
  };

  const handleStopGeneration = () => {
    if (!ws.current || ws.current.readyState !== WebSocket.OPEN) return;
    ws.current.send(JSON.stringify({ type: 'stop' }));
  };

  const handleDeletePair = async (aiMsgIndex: number, aiMsgId?: number, skipConfirm?: boolean) => {
    if (!skipConfirm && !confirm('Remove this interaction?')) return false;
    const prevMsgs = [...messages];
    setMessages(prev => {
      const next = [...prev];
      if (next[aiMsgIndex].role === 'assistant') {
        next.splice(aiMsgIndex, 1);
        if (aiMsgIndex > 0 && next[aiMsgIndex - 1].role === 'user') {
          next.splice(aiMsgIndex - 1, 1);
        }
      }
      return next;
    });
    if (aiMsgId) {
      try {
        const response = await api.delete(`/workshop/chat/message/${activeMode}/${aiMsgId}/`);
        if (!skipConfirm) {
          alert(`删除成功！共删除了 ${response.data.deleted_ids?.length || 0} 条消息`);
        }
        return true;
      } catch (e) {
        if (!skipConfirm) {
          alert('删除失败，请重试');
        }
        setMessages(prevMsgs);
        return false;
      }
    }
    return true;
  };

  const handleDeleteSingle = async (msgIndex: number, msgId?: number, skipConfirm?: boolean) => {
    if (!skipConfirm && !confirm('删除这条消息？')) return false;
    const prevMsgs = [...messages];
    setMessages(prev => {
      const next = [...prev];
      next.splice(msgIndex, 1);
      return next;
    });
    if (msgId) {
      try {
        const response = await deleteSingleChatMessage(activeMode, msgId);
        if (!skipConfirm) {
          alert(`删除成功！消息ID: ${response.data.deleted_id}`);
        }
        return true;
      } catch (e) {
        if (!skipConfirm) {
          alert('删除失败，请重试');
        }
        setMessages(prevMsgs);
        return false;
      }
    }
    return true;
  };

  const handleClearAll = async () => {
    if (!confirm('⚠️ Clear ALL chat history?')) return;
    try {
      const response = await api.delete(`/workshop/chat/clear/${activeMode}/${lessonId}/${artifactId}/`);
      if (response.data.status === 'ok') {
        setMessages([]);
        alert('聊天历史清空成功！');
      } else {
        alert('清空失败，请重试');
      }
    } catch (e) {
      alert('清空失败，请重试');
    }
  };

  const handleDirectSend = (text: string) => sendMessage(text, getEffectivePromptId() || undefined, false);

  const handleToggleLeft = () => {
    const willCollapse = !isLeftPanelCollapsed;
    setIsLeftPanelCollapsed(willCollapse);
    if (willCollapse && isRightPanelCollapsed) {
      setIsRightPanelCollapsed(false);
    }
  };

  const handleToggleRight = () => {
    const willCollapse = !isRightPanelCollapsed;
    setIsRightPanelCollapsed(willCollapse);
    if (willCollapse && isLeftPanelCollapsed) {
      setIsLeftPanelCollapsed(false);
    }
  };

  return (
    <div className="flex h-screen flex-col bg-gray-50">
      <WorkshopHeader
        title={lessonInfo?.title || 'Loading...'}
        isConnected={isConnected}
        onBack={() => window.history.back()}
        onReconnect={connectWebSocket}
      />

      <div className="relative flex flex-1 flex-row overflow-hidden">
        <button
          onClick={handleToggleLeft}
          className="absolute left-0 top-1/2 z-20 flex h-16 w-6 -translate-y-1/2 items-center justify-center rounded-r-lg border border-gray-200 bg-white shadow-md transition-all duration-300 hover:bg-gray-50"
          title={isLeftPanelCollapsed ? '展开聊天区域' : '折叠聊天区域'}
        >
          {isLeftPanelCollapsed ? <PanelLeftOpen size={16} className="text-gray-500" /> : <PanelLeftClose size={16} className="text-gray-500" />}
        </button>

        <ChatArea
          messages={messages}
          prompts={prompts}
          selectedPromptId={selectedPromptId}
          onSelectPrompt={setSelectedPromptId}
          onStart={handleStart}
          onClearHistory={handleClearAll}
          isGenerating={isBusy}
          input={input}
          setInput={setInput}
          onSend={handleSendClick}
          onAddToDraft={addToDraft}
          onDeletePair={handleDeletePair}
          onDeleteSingle={handleDeleteSingle}
          isConnected={isConnected}
          hasLessonInfo={!!lessonInfo}
          isCurrentUserOwner={isCurrentUserOwner}
          onDirectSend={handleDirectSend}
          onAppendContent={handleAppendContent}
          onSaveContent={handleSaveContent}
          isRightPanelCollapsed={isRightPanelCollapsed}
          documentContent={localContent}
          isCollapsed={isLeftPanelCollapsed}
          onExport={handleExportMessage}
          courseId={effectiveCourseId}
          phaseCount={getPhaseCountByCourseId(effectiveCourseId)}
          currentReasoning={currentReasoning}
          onStopGeneration={handleStopGeneration}
        />

        <button
          onClick={handleToggleRight}
          className="absolute right-0 top-1/2 z-20 flex h-16 w-6 -translate-y-1/2 items-center justify-center rounded-l-lg border border-gray-200 bg-white shadow-md transition-all duration-300 hover:bg-gray-50"
          title={isRightPanelCollapsed ? '展开教案区域' : '折叠教案区域'}
        >
          {isRightPanelCollapsed ? <PanelRightOpen size={16} className="text-gray-500" /> : <PanelRightClose size={16} className="text-gray-500" />}
        </button>

        <DocumentEditor
          content={localContent}
          onChange={planActions.updateContent}
          onSave={planActions.savePlan}
          user={user}
          isContentChanged={isContentChanged}
          isCurrentUserOwner={isCurrentUserOwner}
          plans={plans}
          currentPlanId={currentPlan?.id || null}
          onPlanChange={planActions.selectPlan}
          onDeletePlan={planActions.deletePlan}
          isSyncing={isPlanSyncing}
          isCollapsed={isRightPanelCollapsed}
          isLeftPanelCollapsed={isLeftPanelCollapsed}
          onGenerateWithAgents={handleRunMultiAgent}
          onStopAgentGeneration={handleStopMultiAgent}
          isAgentGenerating={isAgentGenerating}
          hasMultiAgentProgress={hasMultiAgentProgress}
          onContinueAgentGeneration={handleContinueMultiAgent}
          onRestartAgentGeneration={handleRestartMultiAgent}
        />
      </div>
    </div>
  );
}
